import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Wand2, Loader2, X, Check, AlertTriangle, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "storylo_admin_token";

/* ──────────────────────────────────────────────────────────────────────────
 * BatchAutoWritePanel
 *
 * Studio-header companion that backfills SEO-rich articles for every
 * published story that doesn't have one yet. One click, walks every
 * eligible video, runs auto-write (transcript + optional donate-link
 * scrape) and saves the result to story_body. Skips any story that
 * already has an article — never overwrites the creator's own writing.
 *
 *  • Pill button in header opens a modal.
 *  • Modal shows pre-flight counts (eligible / already-have / no-source).
 *  • Confirm → backend processes 4 in parallel; we show per-row results.
 *
 * Auth: shares the same `storylo_admin_token` localStorage key as
 * SubmissionsPanel so creators only ever enter the token once.
 * ────────────────────────────────────────────────────────────────────── */
export default function BatchAutoWritePanel({ reloadLibrary }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [eligible, setEligible] = useState(null); // {total_published, has_article, no_source, eligible, eligible_count}
  const [results, setResults] = useState(null); // {processed, succeeded, failed, skipped, results}
  const [err, setErr] = useState("");
  const tokenRef = useRef(null);

  useEffect(() => {
    tokenRef.current = (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_KEY)) || "";
  }, []);

  const ensureToken = () => {
    if (tokenRef.current) return tokenRef.current;
    const t = window.prompt("Admin token (Studio):", "storylo-admin");
    if (!t) return "";
    tokenRef.current = t;
    try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* blocked */ }
    return t;
  };

  const loadEligible = async () => {
    setErr("");
    setLoading(true);
    setResults(null);
    try {
      const token = ensureToken();
      if (!token) { setLoading(false); return; }
      const r = await axios.get(`${API}/admin/auto-write-eligible`, {
        headers: { "X-Admin-Token": token },
      });
      setEligible(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* blocked */ }
        tokenRef.current = null;
        setErr("Admin token rejected. Click again to re-enter.");
      } else {
        setErr(e?.response?.data?.detail || "Couldn't load — try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onOpen = () => {
    setOpen(true);
    loadEligible();
  };

  const onClose = () => {
    if (running) return; // don't close mid-batch — user might think it stopped
    setOpen(false);
    setResults(null);
    setErr("");
  };

  // Escape closes (only when not running).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, running]);

  const onRun = async () => {
    setErr("");
    setRunning(true);
    setResults(null);
    try {
      const token = ensureToken();
      if (!token) { setRunning(false); return; }
      const r = await axios.post(`${API}/admin/auto-write-all-empty`, null, {
        headers: { "X-Admin-Token": token },
        // Generous timeout: 4 parallel × ~6 s each ≈ 1.5 s/story. 200 stories
        // → ~5 min worst case. Match backend's silent infinite tolerance.
        timeout: 600000,
      });
      setResults(r.data);
      if (reloadLibrary) reloadLibrary();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Batch failed — try again.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <button
        onClick={onOpen}
        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-white/15 hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5"
        data-testid="batch-auto-write-open"
        title="Generate articles for every published story without one"
      >
        <Wand2 className="w-3.5 h-3.5" /> Backfill articles
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
          onClick={onClose}
          data-testid="batch-auto-write-modal"
        >
          <div
            className="panel max-w-2xl w-full mx-auto my-12 md:my-6 p-6 md:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Backfill articles"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" /> Article backfill
                </div>
                <div className="font-display text-xl leading-tight">
                  Generate articles for every story without one
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1.5 leading-relaxed">
                  Walks every <em>published</em> story, drafts a 3-paragraph article from its transcript (and donate-link page when available), and saves it. Stories that already have an article are left alone — your writing is never overwritten.
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid="batch-auto-write-close"
                aria-label="Close"
                disabled={running}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-[color:var(--accent)]" />
              </div>
            ) : err && !eligible ? (
              <div className="text-sm text-red-300 p-3 rounded border border-red-500/30 bg-red-500/10" data-testid="batch-auto-write-error">
                {err}
              </div>
            ) : eligible ? (
              <>
                {/* Pre-flight summary — hidden once results are in to avoid stale numbers. */}
                {!results && (
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="panel p-3 border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Eligible</div>
                      <div className="font-display text-2xl mt-1 text-[color:var(--accent)]" data-testid="batch-auto-write-eligible-count">{eligible.eligible_count}</div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5">will be drafted</div>
                    </div>
                    <div className="panel p-3 border border-white/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">Already have one</div>
                      <div className="font-display text-2xl mt-1">{eligible.has_article}</div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5">left untouched</div>
                    </div>
                    <div className="panel p-3 border border-white/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">No source</div>
                      <div className="font-display text-2xl mt-1">{eligible.no_source}</div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5">no transcript / link</div>
                    </div>
                  </div>
                )}

                {!results && eligible.eligible_count > 0 && (
                  <div className="text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 mb-5 inline-flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>Each story takes ~5–10 s to draft. {eligible.eligible_count} stories ≈ {Math.ceil(eligible.eligible_count * 7 / 60)} min. The modal stays open — you can leave the tab open and come back.</span>
                  </div>
                )}

                {/* Eligible list preview (collapsed when too many) */}
                {!results && eligible.eligible_count > 0 && eligible.eligible_count <= 8 && (
                  <ul className="space-y-1 mb-5" data-testid="batch-auto-write-list">
                    {eligible.eligible.map((s) => (
                      <li key={s.id} className="text-xs text-[color:var(--muted)] inline-flex items-center gap-2">
                        <FileText className="w-3 h-3" /> {s.title}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Results */}
                {results && (
                  <div className="mb-5" data-testid="batch-auto-write-results">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="panel p-3 border border-emerald-500/30 bg-emerald-500/5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Drafted</div>
                        <div className="font-display text-2xl mt-1 text-emerald-300">{results.succeeded}</div>
                      </div>
                      <div className="panel p-3 border border-zinc-500/30 bg-zinc-500/5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-300">Skipped</div>
                        <div className="font-display text-2xl mt-1 text-zinc-300">{results.skipped}</div>
                      </div>
                      <div className="panel p-3 border border-red-500/30 bg-red-500/5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-red-300">Failed</div>
                        <div className="font-display text-2xl mt-1 text-red-300">{results.failed}</div>
                      </div>
                    </div>
                    {results.results && results.results.length > 0 && (
                      <ul className="space-y-1.5 max-h-56 overflow-auto pr-2 border-t border-white/5 pt-3">
                        {results.results.map((r) => (
                          <li key={r.id} className="text-xs flex items-start gap-2 leading-relaxed">
                            {r.status === "succeeded" && <Check className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />}
                            {r.status === "skipped" && <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-zinc-400 flex-shrink-0">·</span>}
                            {r.status === "failed" && <AlertTriangle className="w-3.5 h-3.5 text-red-300 mt-0.5 flex-shrink-0" />}
                            <span className={r.status === "succeeded" ? "" : "text-[color:var(--muted)]"}>
                              <strong className="text-white/90">{r.title}</strong>
                              {r.status === "succeeded" && ` · saved ${r.blocks} blocks`}
                              {r.status === "skipped" && ` · ${r.reason}`}
                              {r.status === "failed" && ` · ${r.reason}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {err && (
                  <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mb-3" data-testid="batch-auto-write-error">{err}</div>
                )}

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-white/5">
                  <button
                    onClick={onClose}
                    disabled={running}
                    className="text-xs px-3 py-2 rounded-md border border-white/10 hover:border-white/30 transition-colors disabled:opacity-40"
                    data-testid="batch-auto-write-cancel"
                  >
                    {results ? "Done" : "Cancel"}
                  </button>
                  {!results && (
                    <button
                      onClick={onRun}
                      disabled={running || eligible.eligible_count === 0}
                      className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="batch-auto-write-run"
                    >
                      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      {running
                        ? `Drafting ${eligible.eligible_count}…`
                        : eligible.eligible_count === 0
                          ? "Nothing to draft"
                          : `Draft ${eligible.eligible_count} ${eligible.eligible_count === 1 ? "article" : "articles"}`}
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
